import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import { GroomingAppointment, GroomingServiceType, Patient } from '@/types';
import { useCreateGroomingAppointment, useUpdateGroomingAppointment } from '@/hooks/useGrooming';
import { usePatients, useCreatePatient } from '@/hooks/usePatients';
import { useCreateOwner } from '@/hooks/useOwners';
import { SERVICE_TYPE_LABELS } from '@/services/grooming';
import { User, Dog, Phone, Mail, MapPin, Plus, ChevronDown, ChevronUp } from 'lucide-react';

const groomingSchema = z.object({
  patient_id: z.string().min(1, 'Seleccione una mascota'),
  scheduled_date: z.string().min(1, 'La fecha es requerida'),
  scheduled_time: z.string().min(1, 'La hora es requerida'),
  duration_minutes: z.string().min(1, 'La duración es requerida'),
  service_type: z.string().min(1, 'Seleccione tipo de servicio'),
  notes: z.string().optional(),
});

// Schema para nuevo cliente + mascota
const newClientSchema = z.object({
  owner_first_name: z.string().min(1, 'El nombre es requerido'),
  owner_last_name: z.string().min(1, 'El apellido es requerido'),
  owner_phone: z.string().optional(),
  owner_email: z.string().email('Email inválido').optional().or(z.literal('')),
  owner_address: z.string().optional(),
  pet_name: z.string().min(1, 'El nombre de la mascota es requerido'),
  pet_species: z.string().min(1, 'La especie es requerida'),
  pet_breed: z.string().optional(),
  pet_sex: z.enum(['male', 'female', 'unknown']),
  pet_size: z.enum(['xs', 'small', 'medium', 'large', 'xl']).optional(),
});

type GroomingFormData = z.infer<typeof groomingSchema>;
type NewClientFormData = z.infer<typeof newClientSchema>;

interface Props {
  appointment?: GroomingAppointment | null;
  preselectedPatient?: Patient | null;
  preselectedDate?: string;
  preselectedTime?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const speciesOptions = [
  { value: 'Canino', label: 'Canino' },
  { value: 'Felino', label: 'Felino' },
  { value: 'Otro', label: 'Otro' },
];

const sexOptions = [
  { value: 'male', label: 'Macho' },
  { value: 'female', label: 'Hembra' },
  { value: 'unknown', label: 'No especificado' },
];

const sizeOptions = [
  { value: '', label: 'Seleccionar' },
  { value: 'xs', label: '0-10 kg' },
  { value: 'small', label: '10-20 kg' },
  { value: 'medium', label: '20-30 kg' },
  { value: 'large', label: '30-40 kg' },
  { value: 'xl', label: '+40 kg' },
];

export default function GroomingAppointmentForm({
  appointment,
  preselectedPatient,
  preselectedDate,
  preselectedTime,
  onSuccess,
  onCancel,
}: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [showMoreOwnerFields, setShowMoreOwnerFields] = useState(false);
  const [newClientError, setNewClientError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: patients = [], refetch: refetchPatients } = usePatients({ search: searchTerm || undefined });
  const createAppointment = useCreateGroomingAppointment();
  const updateAppointment = useUpdateGroomingAppointment();
  const createOwner = useCreateOwner();
  const createPatient = useCreatePatient();

  // Formulario principal de turno
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<GroomingFormData>({
    resolver: zodResolver(groomingSchema),
    defaultValues: {
      patient_id: appointment?.patient_id?.toString() || preselectedPatient?.id?.toString() || '',
      scheduled_date: appointment?.scheduled_date || preselectedDate || '',
      scheduled_time: appointment?.scheduled_time?.slice(0, 5) || preselectedTime || '09:00',
      duration_minutes: appointment?.duration_minutes?.toString() || '60',
      service_type: appointment?.service_type || 'bath',
      notes: appointment?.notes || '',
    },
  });

  // Formulario para nuevo cliente
  const {
    register: registerNewClient,
    handleSubmit: handleSubmitNewClient,
    reset: resetNewClient,
    formState: { errors: newClientErrors },
  } = useForm<NewClientFormData>({
    resolver: zodResolver(newClientSchema),
    defaultValues: {
      owner_first_name: '',
      owner_last_name: '',
      owner_phone: '',
      owner_email: '',
      owner_address: '',
      pet_name: '',
      pet_species: 'Canino',
      pet_breed: '',
      pet_sex: 'unknown',
      pet_size: undefined,
    },
  });

  const selectedPatientId = watch('patient_id');
  const selectedPatient = patients.find((p) => p.id.toString() === selectedPatientId);

  // Resetear formulario cuando cambian las props clave
  useEffect(() => {
    reset({
      patient_id: appointment?.patient_id?.toString() || preselectedPatient?.id?.toString() || '',
      scheduled_date: appointment?.scheduled_date || preselectedDate || '',
      scheduled_time: appointment?.scheduled_time?.slice(0, 5) || preselectedTime || '09:00',
      duration_minutes: appointment?.duration_minutes?.toString() || '60',
      service_type: appointment?.service_type || 'bath',
      notes: appointment?.notes || '',
    });
  }, [appointment, preselectedPatient, preselectedDate, preselectedTime, reset]);

  const onSubmit = async (data: GroomingFormData) => {
    setSubmitError(null);
    try {
      const payload = {
        patient_id: parseInt(data.patient_id),
        scheduled_date: data.scheduled_date,
        scheduled_time: data.scheduled_time,
        duration_minutes: parseInt(data.duration_minutes),
        service_type: data.service_type as GroomingServiceType,
        notes: data.notes || undefined,
      };

      if (appointment) {
        await updateAppointment.mutateAsync({ id: appointment.id, data: payload });
      } else {
        await createAppointment.mutateAsync(payload);
      }
      onSuccess();
    } catch (error: any) {
      console.error('Error saving appointment:', error);
      // Error 409 = conflicto de horario
      if (error?.response?.status === 409) {
        setSubmitError(error.response.data?.detail || 'Ya existe un turno en ese horario');
      } else {
        setSubmitError(error?.response?.data?.detail || 'Error al guardar el turno');
      }
    }
  };

  // Crear nuevo cliente + mascota
  const onSubmitNewClient = async (data: NewClientFormData) => {
    setNewClientError(null);
    try {
      // 1. Crear el propietario
      const newOwner = await createOwner.mutateAsync({
        first_name: data.owner_first_name,
        last_name: data.owner_last_name,
        phone: data.owner_phone || undefined,
        email: data.owner_email || undefined,
        address: data.owner_address || undefined,
      });

      // 2. Crear la mascota
      const newPatient = await createPatient.mutateAsync({
        owner_id: newOwner.id,
        name: data.pet_name,
        species: data.pet_species,
        breed: data.pet_breed || undefined,
        sex: data.pet_sex,
        size: data.pet_size || undefined,
      });

      // 3. Refrescar lista de pacientes y seleccionar el nuevo
      await refetchPatients();
      setValue('patient_id', newPatient.id.toString());

      // 4. Limpiar y cerrar el formulario de nuevo cliente
      resetNewClient();
      setShowNewClientForm(false);
      setShowMoreOwnerFields(false);
    } catch (err: any) {
      console.error('Error creating client:', err);
      setNewClientError(err?.response?.data?.detail || 'Error al crear el cliente');
    }
  };

  const serviceTypeOptions = Object.entries(SERVICE_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const durationOptions = [
    { value: '30', label: '30 minutos' },
    { value: '45', label: '45 minutos' },
    { value: '60', label: '1 hora' },
    { value: '90', label: '1.5 horas' },
    { value: '120', label: '2 horas' },
  ];

  const isCreatingClient = createOwner.isPending || createPatient.isPending;

  return (
    <div className="space-y-4">
      {/* Formulario de nuevo cliente (colapsable) */}
      {showNewClientForm ? (
        <div className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary-600" />
              Nuevo Cliente y Mascota
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowNewClientForm(false)}
            >
              Cancelar
            </Button>
          </div>

          {newClientError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {newClientError}
            </div>
          )}

          <form onSubmit={handleSubmitNewClient(onSubmitNewClient)} className="space-y-4">
            {/* Datos del propietario */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <User className="h-4 w-4 text-taupe-600" />
                Datos del Propietario
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Nombre *"
                  placeholder="Nombre"
                  {...registerNewClient('owner_first_name')}
                  error={newClientErrors.owner_first_name?.message}
                />
                <Input
                  label="Apellido *"
                  placeholder="Apellido"
                  {...registerNewClient('owner_last_name')}
                  error={newClientErrors.owner_last_name?.message}
                />
              </div>

              <Input
                label="Teléfono"
                placeholder="Número de contacto"
                {...registerNewClient('owner_phone')}
                error={newClientErrors.owner_phone?.message}
              />

              <button
                type="button"
                onClick={() => setShowMoreOwnerFields(!showMoreOwnerFields)}
                className="flex items-center gap-1 text-sm text-taupe-600 hover:text-taupe-800"
              >
                {showMoreOwnerFields ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Ocultar campos adicionales
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Más campos (Email, Dirección)
                  </>
                )}
              </button>

              {showMoreOwnerFields && (
                <div className="space-y-3">
                  <Input
                    label="Email"
                    type="email"
                    placeholder="email@ejemplo.com"
                    {...registerNewClient('owner_email')}
                    error={newClientErrors.owner_email?.message}
                  />
                  <Input
                    label="Dirección"
                    placeholder="Dirección"
                    {...registerNewClient('owner_address')}
                  />
                </div>
              )}
            </div>

            {/* Datos de la mascota */}
            <div className="space-y-3 pt-3 border-t">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Dog className="h-4 w-4 text-cream-700" />
                Datos de la Mascota
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Nombre *"
                  placeholder="Nombre de la mascota"
                  {...registerNewClient('pet_name')}
                  error={newClientErrors.pet_name?.message}
                />
                <Select
                  label="Especie *"
                  options={speciesOptions}
                  {...registerNewClient('pet_species')}
                  error={newClientErrors.pet_species?.message}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Input
                  label="Raza"
                  placeholder="Ej: Labrador"
                  {...registerNewClient('pet_breed')}
                />
                <Select
                  label="Sexo"
                  options={sexOptions}
                  {...registerNewClient('pet_sex')}
                />
                <Select
                  label="Tamaño"
                  options={sizeOptions}
                  {...registerNewClient('pet_size')}
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" loading={isCreatingClient}>
                <Plus className="h-4 w-4 mr-1" />
                Crear y Seleccionar
              </Button>
            </div>
          </form>
        </div>
      ) : (
        /* Búsqueda y selección de mascota */
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Buscar mascota</label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowNewClientForm(true)}
              className="text-primary-600"
            >
              <Plus className="h-4 w-4 mr-1" />
              Nuevo Cliente
            </Button>
          </div>
          <input
            type="text"
            placeholder="Buscar por nombre de mascota o dueño..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
          />
          <Select
            label="Mascota"
            error={errors.patient_id?.message}
            {...register('patient_id')}
            options={patients.map((p) => ({
              value: p.id.toString(),
              label: `${p.name} (${p.species}${p.breed ? ` - ${p.breed}` : ''}) - ${p.owner?.first_name} ${p.owner?.last_name}`,
            }))}
          />

          {/* Info del dueño seleccionado */}
          {selectedPatient && selectedPatient.owner && (
            <div className="mt-3 p-3 bg-taupe-50 rounded-lg border border-taupe-200">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-taupe-600" />
                <span className="font-medium text-gray-900">
                  {selectedPatient.owner.first_name} {selectedPatient.owner.last_name}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {selectedPatient.owner.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="h-3 w-3 text-gray-400" />
                    {selectedPatient.owner.phone}
                  </div>
                )}
                {selectedPatient.owner.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="h-3 w-3 text-gray-400" />
                    {selectedPatient.owner.email}
                  </div>
                )}
                {selectedPatient.owner.address && (
                  <div className="flex items-center gap-2 text-gray-600 sm:col-span-2">
                    <MapPin className="h-3 w-3 text-gray-400" />
                    {selectedPatient.owner.address}
                  </div>
                )}
              </div>
              {/* Info de la mascota */}
              <div className="mt-2 pt-2 border-t border-taupe-200 text-sm text-gray-600">
                <span className="font-medium">{selectedPatient.name}</span>
                {' - '}
                {selectedPatient.species}
                {selectedPatient.breed && ` (${selectedPatient.breed})`}
                {selectedPatient.sex && selectedPatient.sex !== 'unknown' && (
                  <span className="ml-2">
                    {selectedPatient.sex === 'male' ? 'Macho' : 'Hembra'}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Formulario del turno */}
      {!showNewClientForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error de solapamiento */}
          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
              <span className="text-red-500 font-bold">!</span>
              {submitError}
            </div>
          )}

          {/* Fecha y hora */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Fecha"
              type="date"
              error={errors.scheduled_date?.message}
              {...register('scheduled_date')}
            />
            <Input
              label="Hora"
              type="time"
              error={errors.scheduled_time?.message}
              {...register('scheduled_time')}
            />
          </div>

          {/* Tipo de servicio y duración */}
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Tipo de Servicio"
              error={errors.service_type?.message}
              {...register('service_type')}
              options={serviceTypeOptions}
            />
            <Select
              label="Duración"
              error={errors.duration_minutes?.message}
              {...register('duration_minutes')}
              options={durationOptions}
            />
          </div>

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder="Observaciones sobre el servicio, estado del pelo, etc."
              {...register('notes')}
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={isSubmitting || createAppointment.isPending || updateAppointment.isPending}
            >
              {appointment ? 'Guardar Cambios' : 'Crear Turno'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
